import { Texture, Text, TextStyle, Container, IDestroyOptions, Sprite, Point } from "pixi.js";
import { IMarketplaceBuildingModel } from "../../models/building/IMarketplaceBuildingModel";
import { nameof } from "bindable-data";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { gsap } from "gsap";
import { IMarketplaceViewModel } from "../../models/IMarketplaceViewModel";
import { FullscreenInputBlocker } from "../../../../../game/components/FullscreenInputBlocker";
import { MarketplaceResourcePackage } from "../../../resources/MarketplaceResourcePackage";

export class FullBuilding extends Container {
    private static readonly BUILDING_SIZE = new Point(798, 798);

    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly marketplaceBuildingModel: IMarketplaceBuildingModel;
    private readonly marketplaceViewModel: IMarketplaceViewModel;
    private readonly fullScreenInputBlocker: FullscreenInputBlocker;
    private level: Text;
    private building: Sprite;
    private loaderContainer: Container;
    private loaderBg: Sprite;
    private loader: Sprite;
    private loaderAnimation: gsap.core.Timeline;
    private hideAnimation: gsap.core.Timeline;

    public constructor(
        marketplaceResources: MarketplaceResourcePackage,
        marketplaceBuildingModel: IMarketplaceBuildingModel,
        marketplaceViewModel: IMarketplaceViewModel,
        fullScreenInputBlocker: FullscreenInputBlocker
    ) {
        super();
        this.marketplaceResources = marketplaceResources;
        this.marketplaceBuildingModel = marketplaceBuildingModel;
        this.marketplaceViewModel = marketplaceViewModel;
        this.fullScreenInputBlocker = fullScreenInputBlocker;
        this.marketplaceBuildingModel.propertyChanged.add(this.onMarketplaceBuildingModelPropertyChanged, this);
        this.build();
    }

    private async onMarketplaceBuildingModelPropertyChanged(model: IMarketplaceBuildingModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "currentBuilding"):
                this.showLoader();
                const currentBuilding = model.currentBuilding.value.building;
                this.level.text = `Rank ${currentBuilding.level}`;
                if (this.marketplaceViewModel.selectedMarketplaceCard.value.listItem.id === model.currentBuilding.value.id) {
                    const texture = await this.loadBuildingTexture(currentBuilding.imageUrl);
                    this.building.texture = texture;
                    this.level.position.set(0, -290);
                    this.hideLoader();
                }
                break;
        }
    }

    private build(): void {
        this.building = new Sprite(this.marketplaceResources.assets_views_marketplace_fullbuilding_png);
        this.building.width = FullBuilding.BUILDING_SIZE.x;
        this.building.height = FullBuilding.BUILDING_SIZE.y;
        this.building.eventMode = "static";

        this.building.anchor.set(0.5);
        this.addChild(this.building);

        this.level = new Text("Rank 10", new LevelTextStyle());
        this.level.anchor.set(0.5);
        this.level.angle = -8;
        this.level.skew._x = -0.087;
        this.building.addChild(this.level);

        this.loaderContainer = new Container();
        this.addChild(this.loaderContainer);

        this.loaderBg = new Sprite(Texture.WHITE);
        this.loaderBg.width = this.building.width;
        this.loaderBg.height = this.building.height;
        this.loaderBg.anchor.set(0.5);
        this.loaderBg.tint = 0x000000;
        this.loaderBg.alpha = 0.85;
        this.loaderContainer.addChild(this.loaderBg);

        this.loader = new Sprite(this.marketplaceResources.assets_views_marketplace_loader_png);
        this.loader.anchor.set(0.5);
        this.loader.scale.set(2);
        this.loaderContainer.addChild(this.loader);

        this.hideAnimation = new gsap.core.Timeline({ paused: true });
        this.loaderAnimation = new gsap.core.Timeline({ paused: true });
        this.loaderAnimation.to(this.loader, { angle: 360, repeat: -1, duration: 2, ease: "power1.inOut" });
    }

    public showLoader(): void {
        this.fullScreenInputBlocker.blocked = true;
        this.loaderContainer.alpha = 1;
        this.loaderAnimation?.play();
    }

    public async hideLoader(): Promise<void> {
        this.hideAnimation.to(this.loaderContainer, { alpha: 0, duration: 0.6 });
        await this.hideAnimation.play();
        this.hideAnimation?.clear();
        this.loaderAnimation?.pause(0);
        this.fullScreenInputBlocker.blocked = false;
    }

    private async loadBuildingTexture(textureUrl: string): Promise<Texture> {
        const cardDynamicResources = new DynamicResourcesPackage();
        cardDynamicResources.add(textureUrl);
        await cardDynamicResources.load();
        return cardDynamicResources.resource(textureUrl);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.marketplaceBuildingModel.propertyChanged.remove(this.onMarketplaceBuildingModelPropertyChanged, this);
        this.loaderAnimation?.kill();
        this.hideAnimation?.kill();
        super.destroy(options);
    }
}

class LevelTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAlpha: 0.6,
            dropShadowAngle: 0,
            dropShadowBlur: 15,
            dropShadowDistance: 0,
            padding: 20,
            fill: "#ffffff",
            fontFamily: "COMICZ",
            strokeThickness: 1,
            fontSize: 64
        }));
    }
}
